import { getChromeWebRequest } from '../chrome/wrappers';
import { isFullyConfigured } from '../helper/authentication';
import { matchesAuthorizationUrl } from '../chrome/routing';
import { extractPayload } from '../lib/parser/androidPayloadParser';
import { authenticateByToken } from '../actions/authenticationActions';
import { initAccount } from '../actions/accountActions';
import {
  getOrCreateDeviceAndProfile,
  getOrCreateUserDeviceUId,
  initDeviceAndUser,
} from '../actions/deviceUserActions';
import { initWhenFullyConfigured } from '../background';
import { sendExtensionConfiguredMessage } from '../actions/onboardingActions';
import { captureException } from '../helper/sentry';
import { closeTab } from '../chrome/tabManager';
import {
  authenticateByDelegationToken,
  authenticateByPassword,
} from '../api/authentication';
import { getDelegationStore } from '../storage/stores';
import { uuidv4 } from '../lib/wrappers';

export const setupAuthentication = () => {
  getChromeWebRequest().onBeforeRequest.addListener(onAuthenticationRequest, {
    urls: ['http://*/*', 'https://*/*'],
    types: ['main_frame'],
  });
};

const onAuthenticationRequest = (
  details: chrome.webRequest.WebRequestBodyDetails
) => {
  // Authenticate if not already, and auth URL is received
  if (!isFullyConfigured() && matchesAuthorizationUrl(details.url)) {
    authenticate(details);
  }

  // We don't want to block the request
  return { cancel: false };
};

/**
 * Onboard the extension using a URL with a token + deviceUid
 */
export const authenticate = async (
  details: chrome.webRequest.WebRequestBodyDetails
) => {
  try {
    const payload = extractPayload(details.url);
    await authenticateByToken(payload.token);
    await initAccount();
    await initDeviceAndUser(payload.deviceUid);
    await initWhenFullyConfigured(true, false);
    sendExtensionConfiguredMessage();
  } catch (error) {
    console.error(error);
    captureException(error as Error);
  } finally {
    closeTab(details.tabId);
  }
};

/**
 * Onboard the extension using a username + password + device name + profile name
 *
 * The profile and devices will be created if they are missing.
 */
export const fullOnboardingWithCredentials = async (
  username: string,
  password: string,
  deviceName: string,
  profileName: string
) => {
  try {
    await authenticateByPassword(username, password);
    await initAccount();
    const deviceUid = await getOrCreateDeviceAndProfile(
      deviceName,
      profileName,
      'android_test'
    );
    await initDeviceAndUser(deviceUid);
    await initWhenFullyConfigured(true, false);
    sendExtensionConfiguredMessage();
  } catch (error) {
    console.error(error);
    captureException(error as Error);
  }
};

/**
 * Onboard the extension using a JWT delegation token.
 *
 * @remarks
 * It will authenticate to QAPI using the provided token, create & assign a device using the returned profile ID,
 * and fully onboard the extension.
 */
export const fullOnboardingWithDelegation = async (token: string) => {
  console.log('[Delegation onboarding] Starting');
  const profileId = await authenticateByDelegationToken(token);
  console.log(
    `[Delegation onboarding] Authentication completed, profileId=${profileId}`
  );
  await initAccount();
  const deviceUid = await getOrCreateUserDeviceUId(
    uuidv4(),
    profileId,
    'delegated_chromebook'
  );
  console.log(
    `[Delegation onboarding] New device created, deviceUid=${deviceUid}`
  );
  await initDeviceAndUser(deviceUid);
  // At this point, the device is already logged in.
  // So we do not need to onboard again if anything fails from here.
  await getDelegationStore().setIsLoggedIn(true);
  console.log(`[Delegation onboarding] Finished`);
  return initWhenFullyConfigured(true, true);
};
